import { useState, useMemo } from 'react';
import { Button } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useSelector, useDispatch } from 'react-redux';

import RunSummaryExportModal from './RunSummaryExportModal';
import { fetchRunSummaryExport } from '../../../services/activeRunsService';
import { handleDownloadRunSummaryCSV } from '../../../utilities/export/handleDownloadRunSummaryCSV';
import { toBackendDate } from '../summary/utils/runSummaryUtils';
import {
  getBuildVersionsForFilters,
  selectBuildsFilters,
} from '../../../redux/runSummary/runSummarySlice';
import { selectMarket } from '../../../redux/app/configSlice';
import { filterDevicesByMarket } from '../../../services/flowApiService';
import { selectDevices, getDevicesAsync } from '../../../redux/devices/getDevicesSlice';
import { getDeviceIdForAppiumScripts } from '../../../utilities/getDevideIdForAppiumScripts';

export const RunSummaryExport = () => {
  const dispatch = useDispatch();
  const filtersVersion = useSelector(selectBuildsFilters);
  const market = useSelector(selectMarket);
  const devices = useSelector(selectDevices);

  // Devices and build versions are only needed inside the export modal, so fetch
  // them lazily when the user opens it (and only if not already cached in Redux),
  // instead of on page load.
  const handleOpenExport = () => {
    if (devices.length === 0) {
      dispatch(getDevicesAsync());
    }
    const hasBuilds =
      (filtersVersion.ios?.length ?? 0) > 0 || (filtersVersion.android?.length ?? 0) > 0;
    if (!hasBuilds) {
      dispatch(getBuildVersionsForFilters());
    }
    setExportOpen(true);
  };

  const deviceOptions = useMemo(
    () =>
      filterDevicesByMarket(devices, market?.id)
        .filter((device) => !device.deleted_at)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((device) => ({
          id: device.id,
          type: device.type,
          name: device.name,
          value: getDeviceIdForAppiumScripts(device),
        })),
    [devices, market],
  );

  const [exportOpen, setExportOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState(null);

  const handleExport = async ({ deviceId, appVersion, startDate, endDate }) => {
    setExportLoading(true);
    setExportError(null);
    try {
      const data = await fetchRunSummaryExport({
        deviceId: deviceId || null,
        appVersion: appVersion || null,
        startDate: toBackendDate(startDate),
        endDate: toBackendDate(endDate),
      });
      handleDownloadRunSummaryCSV(data.runs || [], deviceOptions);
      setExportOpen(false);
    } catch (err) {
      setExportError(err?.response?.data?.detail || err?.message || 'Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<FileDownloadIcon />}
        onClick={handleOpenExport}
        sx={{ ml: 1, textTransform: 'none', fontWeight: 600 }}
      >
        Export
      </Button>

      <RunSummaryExportModal
        open={exportOpen}
        onClose={() => {
          setExportOpen(false);
          setExportError(null);
        }}
        onExport={handleExport}
        deviceOptions={deviceOptions}
        filtersVersion={filtersVersion}
        loading={exportLoading}
        error={exportError}
      />
    </>
  );
};
